/**
 * The only urgency the trial CTAs may show: things someone can point at a
 * roster or a calendar to prove. Replaces the monthly countdown that reset
 * itself (misleading conduct under Australian Consumer Law, see CLAUDE.md).
 *
 * To run a concert push: set NEXT_CONCERT. It self-retires once `enrolBy`
 * passes, and the CTAs fall back to the peak-times line, so nothing ever resets.
 */

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export const URGENCY_TIMEZONE = 'Australia/Adelaide';

export interface Concert {
  /** e.g. "end-of-year concert" */
  name: string;
  /** Human date for copy, e.g. "Saturday 13 December" */
  dateLabel: string;
  /** Adelaide calendar date (YYYY-MM-DD): the last day a new student can start and still be ready to perform. */
  enrolBy: string;
  /** Human form of enrolBy, e.g. "26 October" */
  enrolByLabel: string;
}

/**
 * null until staff confirm the date and the readiness cutoff. Never guess either.
 * 2026: date from Notion ME-1902; cutoff is the portal's MEMBERSHIP_ENROL_BY, so the two pages agree.
 */
export const NEXT_CONCERT: Concert | null = {
  name: 'end-of-year concert',
  dateLabel: 'Saturday 12 December',
  enrolBy: '2026-10-31',
  enrolByLabel: '31 October',
};

/**
 * Prod, 8 weeks to 2026-10-02: 73% of lessons start weekdays 3-7pm or
 * Saturday 9am-1pm, while weekday daytimes sit near empty. Weekly times are
 * first come, first served (portal pricing page). Re-measure before rewording.
 */
const PEAK_TIMES_LINE =
  'After-school and Saturday-morning times are the most requested. Weekly recurring times are first come, first served.';

/** Same measurement, read for working adults: daytimes are the open ones. */
const ADULT_TIMES_LINE =
  'Daytime lesson times are the easiest to get. After-school and Saturday-morning times go first.';

export type UrgencyAudience = 'everyone' | 'adults';

/** True through the end of the enrol-by day in Adelaide. */
export const isConcertOpen = (concert: Concert, now: number = Date.now()): boolean =>
  now <= dayjs.tz(concert.enrolBy, URGENCY_TIMEZONE).endOf('day').valueOf();

/** Copy that is true without a clock: safe to render on the server. */
export const peakTimesLine = (audience: UrgencyAudience = 'everyone'): string =>
  audience === 'adults' ? ADULT_TIMES_LINE : PEAK_TIMES_LINE;

/** The concert line while its deadline holds, otherwise null. */
export const concertLine = (
  concert: Concert | null = NEXT_CONCERT,
  now: number = Date.now(),
): string | null =>
  concert && isConcertOpen(concert, now)
    ? `Start by ${concert.enrolByLabel} to perform at our ${concert.name} on ${concert.dateLabel}.`
    : null;
